import { Box, Card, Grid, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Avatar, Chip, Stack } from "@mui/material";
import React, { useState, useEffect } from "react";
import DashboardCard from "../../components/card/dashboardCard";
import TaskCompletionChart from "../../components/charts/redical";
import TaskColumnChart from "../../components/charts/taskColumnChart";
import { getDashboardData, getActiveWorkersToday } from "../../services/Auth";
import AppLoader from "../../components/appLoader";

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [activeWorkersList, setActiveWorkersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [dashRes, workersRes] = await Promise.all([
        getDashboardData(),
        getActiveWorkersToday()
      ]);

      if (dashRes.status === 200 || dashRes.status === 201) {
        setDashboardData(dashRes.data.data);
      }
      if (workersRes.status === 200 || workersRes.status === 201) {
        setActiveWorkersList(workersRes.data.data || []);
      }
    } catch (err) {
      setError("Error fetching dashboard data");
      console.error("Dashboard data fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const { overview, charts } = dashboardData || {};

  const cardData = [
    {
      title: "Total Employees",
      value: overview?.totalEmployees?.toString() || "0",
      subtext: "Total registered employees",
      subtextColor: "#2e7d32",
    },
    {
      title: "Active Today",
      value: overview?.activeToday?.toString() || "0",
      subtext: "Currently working",
      subtextColor: overview?.activeToday > 0 ? "#2e7d32" : "#9e9e9e",
    },
    {
      title: "Late Check-Ins",
      value: overview?.lateCheckIns?.toString() || "0",
      subtext: "Arrived late today",
      subtextColor: overview?.lateCheckIns > 0 ? "error.main" : "#2e7d32",
    },
    {
      title: "On Break Now",
      value: overview?.onBreakNow?.toString() || "0",
      subtext: "Currently on break",
      subtextColor: overview?.onBreakNow > 0 ? "#ff9800" : "#2e7d32",
    },
    {
      title: "Tasks Assigned Today",
      value: overview?.tasksAssignedToday?.toString() || "0",
      subtext: "New tasks today",
      subtextColor: "#2e7d32",
    },
    {
      title: "Tasks Completed Today",
      value: overview?.tasksCompleted?.toString() || "0",
      subtext: "Completed today",
      subtextColor: "#2e7d32",
    },
  ];

  return (
    <>
      <Box
        bgcolor="primary.bgLight"
        padding={"20px"}
        borderRadius={"22px"}
        mt={0}
      >
        <AppLoader isVisible={loading} />
        <Grid container spacing={2}>
          <Grid item sm={12} md={5}>
            <Typography variant="h4">Dashboard</Typography>
            <Typography variant="description">
              Quick insight and control center for workplace operations
            </Typography>
          </Grid>
          <Grid item sm={12} spacing={2} md={7}></Grid>
        </Grid>

        <Grid container spacing={2} mt={2}>
          {cardData.map((card, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <DashboardCard {...card} />
            </Grid>
          ))}
        </Grid>


                {/* Active Workers Today Table (Session-wise rows, no Session/Task columns) */}
        <Card
          sx={{
            background: "#fff",
            borderRadius: 4,
            p: 3,
            boxShadow: "6px 6px 15px rgba(0, 0, 0, 0.2)",
            mt: 3,
          }}
        >
          <Typography variant="h6" fontWeight={700} mb={2}>
            Active Workers Today
          </Typography>
          {activeWorkersList.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={80}>
              <Typography color="text.secondary">No workers have checked in today.</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: "1px solid #e0e0e0" }}>
              <Table size="small" aria-label="active workers table">
                <TableHead>
                  <TableRow sx={{ background: "#225B27" }}>
                    <TableCell sx={{ color: "#fff", fontWeight: 700, whiteSpace: "nowrap" }}>Worker</TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: 700, whiteSpace: "nowrap" }}>Designation</TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: 700, whiteSpace: "nowrap" }}>Department</TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: 700, whiteSpace: "nowrap" }}>Status</TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: 700, whiteSpace: "nowrap" }}>Check In</TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: 700, whiteSpace: "nowrap" }}>Break In</TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: 700, whiteSpace: "nowrap" }}>Break Out</TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: 700, whiteSpace: "nowrap" }}>Check Out</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeWorkersList.map((worker) =>
                    (worker.sessions || []).map((session, sIdx) => {
                      const sessionsCount = worker.sessions?.length || 1;
                      return (
                        <TableRow
                          key={`${worker.workerId}-s${session.sessionNumber}`}
                          sx={{
                            "&:hover": { background: "#f9fbe7" },
                            borderTop: sIdx === 0 ? "2px solid #e8f5e9" : "none",
                          }}
                        >
                          {/* Worker info only on first session row */}
                          {sIdx === 0 ? (
                            <TableCell rowSpan={sessionsCount} sx={{ verticalAlign: "top", pt: 1.5 }}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Avatar
                                  src={worker.profileImage || undefined}
                                  sx={{ width: 34, height: 34, bgcolor: "#225B27", fontSize: 14 }}
                                >
                                  {!worker.profileImage && worker.name?.charAt(0)?.toUpperCase()}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" fontWeight={600} noWrap>
                                    {worker.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {worker.employeeId}
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>
                          ) : null}
                          {sIdx === 0 ? (
                            <TableCell rowSpan={sessionsCount} sx={{ verticalAlign: "top", pt: 1.5 }}>
                              <Typography variant="body2" noWrap>{worker.designation}</Typography>
                            </TableCell>
                          ) : null}
                          {sIdx === 0 ? (
                            <TableCell rowSpan={sessionsCount} sx={{ verticalAlign: "top", pt: 1.5 }}>
                              <Typography variant="body2" noWrap>{worker.department}</Typography>
                            </TableCell>
                          ) : null}
                          {sIdx === 0 ? (
                            <TableCell rowSpan={sessionsCount} sx={{ verticalAlign: "top", pt: 1.5 }}>
                              <Chip
                                label={worker.status}
                                size="small"
                                sx={{
                                  bgcolor:
                                    worker.status === "Active"
                                      ? "#e8f5e9"
                                      : worker.status === "On Break"
                                      ? "#fff3e0"
                                      : "#f5f5f5",
                                  color:
                                    worker.status === "Active"
                                      ? "#2e7d32"
                                      : worker.status === "On Break"
                                      ? "#e65100"
                                      : "#757575",
                                  fontWeight: 600,
                                  fontSize: 11,
                                }}
                              />
                            </TableCell>
                          ) : null}
                          <TableCell>
                            <Typography variant="body2" color="#2e7d32" fontWeight={500}>
                              {session.checkIn}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {!session.breakIn || session.breakIn.length === 0 ? (
                              <Typography variant="body2" color="text.disabled">—</Typography>
                            ) : (
                              <Stack spacing={0.3}>
                                {session.breakIn.map((t, i) => (
                                  <Typography key={i} variant="body2" color="#e65100" fontWeight={500}>{t}</Typography>
                                ))}
                              </Stack>
                            )}
                          </TableCell>
                          <TableCell>
                            {!session.breakOut || session.breakOut.length === 0 ? (
                              <Typography variant="body2" color="text.disabled">—</Typography>
                            ) : (
                              <Stack spacing={0.3}>
                                {session.breakOut.map((t, i) => (
                                  <Typography key={i} variant="body2" color="#1565c0" fontWeight={500}>{t}</Typography>
                                ))}
                              </Stack>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color={session.checkOut === "-" ? "text.disabled" : "#b71c1c"}
                              fontWeight={session.checkOut !== "-" ? 500 : 400}
                            >
                              {session.checkOut}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        <Card
          sx={{
            background: "#fff",
            borderRadius: 4,
            minHeight: 130,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            p: 2,
            boxShadow: "6px 6px 15px rgba(0, 0, 0, 0.2)",
            mt: 2,
          }}
        >
          <Grid container spacing={4}>
            <Grid item xs={12} md={7}>
              <TaskColumnChart data={charts?.taskAnalyticsByMonth} />
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <TaskCompletionChart data={charts?.taskCompletion} />
            </Grid>
          </Grid>
        </Card>


      </Box>
    </>
  );
};

export default Dashboard;
